import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Calculator = () => {
  const [display, setDisplay] = useState('0');
  const [equation, setEquation] = useState('');

  const handleNumber = (num) => {
    setDisplay(display === '0' ? String(num) : display + num);
  };

  const handleOperator = (op) => {
    setEquation(display + op);
    setDisplay('0');
  };

  const calculate = () => {
    try {
      // Using Function constructor instead of eval for better security
      const result = new Function('return ' + equation + display)();
      setDisplay(String(result));
      setEquation('');
    } catch (e) {
      setDisplay('Error');
    }
  };

  const clear = () => {
    setDisplay('0');
    setEquation('');
  };

  const buttons = [
    '7', '8', '9', '÷',
    '4', '5', '6', '×',
    '1', '2', '3', '-',
    '0', '.', '=', '+'
  ];

  return (
    <div className="p-2 bg-gray-200 h-full flex flex-col">
      <div className="bg-green-900 text-green-400 p-2 mb-2 font-mono text-right">
        {equation && <div className="text-sm opacity-75">{equation}</div>}
        {display}
      </div>
      <div className="grid grid-cols-4 gap-1">
        <button 
          className="bg-gray-300 hover:bg-gray-400 border-t-2 border-l-2 border-white border-r-2 border-b-2 border-gray-700 p-2"
          onClick={clear}
        >
          C
        </button>
        {buttons.map((btn, i) => (
          <button
            key={i}
            className="bg-gray-300 hover:bg-gray-400 border-t-2 border-l-2 border-white border-r-2 border-b-2 border-gray-700 p-2"
            onClick={() => {
              if (btn === '=') calculate();
              else if (['+', '-', '×', '÷'].includes(btn)) {
                handleOperator(btn === '×' ? '*' : btn === '÷' ? '/' : btn);
              } else handleNumber(btn);
            }}
          >
            {btn}
          </button>
        ))}
      </div>
    </div>
  );
};

const Notepad = () => {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('Untitled.txt');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleSave = () => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleOpen = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => setText(e.target.result);
      reader.readAsText(file);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-200">
      <div className="bg-gray-300 p-1 flex gap-2 relative">
        <button 
          className="px-2 hover:bg-gray-400"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          File
        </button>
        {isMenuOpen && (
          <div className="absolute top-full left-0 bg-gray-200 border-2 border-gray-400 shadow-lg z-50">
            <label className="block px-4 py-1 hover:bg-blue-800 hover:text-white cursor-pointer">
              Open
              <input 
                type="file" 
                accept=".txt"
                className="hidden"
                onChange={handleOpen}
              />
            </label>
            <button 
              className="block w-full px-4 py-1 text-left hover:bg-blue-800 hover:text-white"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        )}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="flex-1 p-2 font-mono bg-white resize-none outline-none"
        style={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word'
        }}
      />
      <div className="bg-gray-300 px-2 py-0.5 text-sm">
        {fileName} - {text.length} characters
      </div>
    </div>
  );
};

const Paint = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(2);
  const [tool, setTool] = useState('brush');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    
    // Set canvas size
    const parent = canvas.parentElement;
    canvas.width = parent.clientWidth - 20;
    canvas.height = parent.clientHeight - 60;
    
    // Set white background
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(
      e.clientX - rect.left,
      e.clientY - rect.top
    );
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.lineTo(
      e.clientX - rect.left,
      e.clientY - rect.top
    );
    ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : color;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveCanvas = () => {
    const canvas = canvasRef.current;
    const image = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = 'drawing.png';
    link.href = image;
    link.click();
  };

  const colors = ['#000000', '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'];

  return (
    <div className="h-full flex flex-col bg-gray-200 p-2">
      <div className="flex gap-2 mb-2">
        <div className="flex gap-1">
          <button
            className={`w-8 h-8 border-2 ${tool === 'brush' ? 'border-blue-500' : 'border-gray-400'}`}
            onClick={() => setTool('brush')}
          >
            🖌️
          </button>
          <button
            className={`w-8 h-8 border-2 ${tool === 'eraser' ? 'border-blue-500' : 'border-gray-400'}`}
            onClick={() => setTool('eraser')}
          >
            ⬜
          </button>
        </div>
        <div className="flex gap-1">
          {colors.map((c) => (
            <button
              key={c}
              className={`w-8 h-8 border-2 ${color === c ? 'border-blue-500' : 'border-gray-400'}`}
              style={{ backgroundColor: c }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>
        <input
          type="range"
          min="1"
          max="20"
          value={brushSize}
          onChange={(e) => setBrushSize(e.target.value)}
          className="w-24"
        />
        <button
          className="px-2 bg-gray-300 border-2 border-gray-400"
          onClick={clearCanvas}
        >
          Clear
        </button>
        <button
          className="px-2 bg-gray-300 border-2 border-gray-400"
          onClick={saveCanvas}
        >
          Save
        </button>
      </div>
      <canvas
        ref={canvasRef}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        className="bg-white flex-1 border border-gray-400 cursor-crosshair"
      />
    </div>
  );
};

const Desktop = () => {
  const [windows, setWindows] = useState([]);
  const [nextZIndex, setNextZIndex] = useState(100);
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [icons, setIcons] = useState([
    { name: 'Paint', icon: 'https://cdn.4fingerstudios.com/assets/images/paint-icon.png', pos: { x: 20, y: 20 } },
    { name: 'Calculator', icon: 'https://cdn.4fingerstudios.com/assets/images/my-computer.png', pos: { x: 20, y: 100 } },
    { name: 'Notepad', icon: 'https://cdn.4fingerstudios.com/assets/images/file-1.png', pos: { x: 20, y: 180 } },
    { name: 'Snake', icon: 'https://cdn.4fingerstudios.com/assets/images/snake-icon.png', pos: { x: 20, y: 260 } }
  ]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const openWindow = (app) => {
    const newWindow = {
      id: Date.now(),
      title: app,
      zIndex: nextZIndex,
      left: 50 + (windows.length * 30),
      top: 50 + (windows.length * 30),
      width: 400,
      height: 300,
      isMaximized: false
    };
    setWindows([...windows, newWindow]);
    setNextZIndex(nextZIndex + 1);
    setStartMenuOpen(false);
  };

  const closeWindow = (id) => {
    setWindows(windows.filter(w => w.id !== id));
  };

  const maximizeWindow = (id) => {
    setWindows(windows.map(w => 
      w.id === id ? {...w, isMaximized: !w.isMaximized} : w
    ));
  };

  const bringToFront = (window) => {
    if (window.zIndex === nextZIndex - 1) return;
    setWindows(windows.map(w => 
      w.id === window.id ? {...w, zIndex: nextZIndex} : w
    ));
    setNextZIndex(nextZIndex + 1);
  };

  return (
    <div className="relative w-full h-screen overflow-hidden select-none bg-teal-600">
      {/* Desktop Icons */}
      <div className="p-4 grid grid-cols-1 gap-4">
        {icons.map(app => (
          <button
            key={app.name}
            className="flex flex-col items-center w-20 text-white"
            onClick={() => openWindow(app.name)}
          >
            <img src={app.icon} alt={app.name} className="w-8 h-8 mb-1" />
            <span className="text-sm bg-blue-900 px-1">{app.name}</span>
          </button>
        ))}
      </div>

      {/* Windows */}
      {windows.map(window => (
        <div
          key={window.id}
          style={{
            position: 'absolute',
            left: window.left,
            top: window.top,
            width: window.isMaximized ? '100%' : window.width,
            height: window.isMaximized ? 'calc(100% - 28px)' : window.height,
            zIndex: window.zIndex
          }}
          className="bg-gray-200 border-2 border-gray-100 shadow-lg"
        >
          <div 
            className="bg-blue-800 text-white px-2 py-1 flex justify-between items-center cursor-move"
            onMouseDown={() => bringToFront(window)}
          >
            <span>{window.title}</span>
            <div className="flex gap-1">
              <button className="w-5 h-4 bg-gray-200 text-black text-xs">_</button>
              <button 
                className="w-5 h-4 bg-gray-200 text-black text-xs"
                onClick={() => maximizeWindow(window.id)}
              >
                □
              </button>
              <button 
                className="w-5 h-4 bg-gray-200 text-black text-xs"
                onClick={() => closeWindow(window.id)}
              >
                ×
              </button>
            </div>
          </div>
          <div className="h-full">
            {window.title === 'Calculator' && <Calculator />}
            {window.title === 'Notepad' && <Notepad />}
            {window.title === 'Paint' && <Paint />}
            {window.title === 'Snake' && <div>Snake Game</div>}
          </div>
        </div>
      ))}

      {/* Taskbar */}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gray-200 border-t border-white flex items-center px-2">
        <button 
          onClick={() => setStartMenuOpen(!startMenuOpen)}
          className="px-4 h-6 bg-gray-300 border-2 border-gray-400 mr-2"
        >
          Start
        </button>
        
        <div className="flex-1 flex gap-2">
          {windows.map(window => (
            <button
              key={window.id}
              onClick={() => bringToFront(window)}
              className="px-4 h-6 bg-gray-300 border-2 border-gray-400 text-sm"
            >
              {window.title}
            </button>
          ))}
        </div>

        <div className="text-sm border-l border-gray-600 pl-2">
          {currentTime.toLocaleTimeString()}
        </div>
      </div>

      {/* Start Menu */}
      {startMenuOpen && (
        <div className="absolute bottom-8 left-0 w-48 bg-gray-200 border-2 border-gray-400 shadow-lg">
        {icons.map(app => (
          <button
            key={app.name}
            onClick={() => openWindow(app.name)}
            className="w-full px-4 py-1 text-left hover:bg-blue-800 hover:text-white"
          >
            {app.name}
          </button>
        ))}
      </div>
    )}

    {/* CRT Effects */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute inset-0 bg-scanlines opacity-5"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent"></div>
    </div>

    <style>{`
      .bg-scanlines {
        background-image: repeating-linear-gradient(
          0deg,
          rgba(0, 0, 0, 0.2) 0px,
          rgba(0, 0, 0, 0.2) 1px,
          transparent 1px,
          transparent 2px
        );
        background-size: 100% 2px;
      }
    `}</style>
  </div>
);
};

export default Desktop;
        